import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { bindActionCreators, compose } from 'redux'
import { connect } from 'react-redux'
import { desktop } from '../../styles/media'
import Icon from '../../components/general/Icon'
import Tag from '../../components/general/Tag'
import DropDown, {
	DropDownLink,
	DropDownLinkCheck,
	DropDownLinkRight,
	HeaderLinkDropDownHeading
} from '../../components/button/DropDown'
import { changeCardNameLanguage, changeLanguage } from '../../state/actionCreators/app'
import languages from '../../data/languages'
import { cardNameLanguageMapping, domains, translationsMapping } from '../../../config'
import translate from '../../helper/translate'
import { events, trackEvent } from '../../helper/track'

const LanguageDropDown = ({ authToken, changeLanguage, changeCardNameLanguage, t, userLanguage }) => (
	<Wrapper>
		<DropDown
			button={(
				<StyledLanguageDropDown>
					<Icon name="globe" />
					.{translationsMapping[userLanguage] === 'en' ? 'com' : translationsMapping[userLanguage]}
				</StyledLanguageDropDown>
			)}
			top={32}
		>
			{onBeforeNavigate => (
				<>
					<HeaderLinkDropDownHeading>
						{t('general.language')}
					</HeaderLinkDropDownHeading>
					{['en-US', 'en-GB', 'de-DE', 'fr-FR', 'it-IT', 'es-ES', 'pt-PT']
						.map((key) => {
							const value = languages[key]
							const domain = domains[cardNameLanguageMapping[key]]

							return (
								<DropDownLink
									onClick={() => {
										if (authToken || window.location.host === domain) {
											changeLanguage(key)
											changeCardNameLanguage(cardNameLanguageMapping[key])
										} else {
											trackEvent(events.SITE_CHANGE_DOMAIN, { domain })

											window.location.href = `https://${domain}${location.pathname !== '/' ? location.pathname : ''}`
										}
									}}
									onBeforeNavigate={onBeforeNavigate}
									active={userLanguage === key}
									key={key}
								>
									<img src={value.flag} alt="" />
									<span>
										<DropDownLinkInner>
											{!authToken && (
												<DropDownLinkInnerBottom>
													{domain}
												</DropDownLinkInnerBottom>
											)}
											<DropDownLinkInnerTop>
												{value.nameLong}
											</DropDownLinkInnerTop>
										</DropDownLinkInner>
									</span>
									<DropDownLinkRight>
										<DropDownLinkCheck active={userLanguage === key}>
											<Icon name="check" />
										</DropDownLinkCheck>
									</DropDownLinkRight>
								</DropDownLink>
							)
						})}
				</>
			)}
		</DropDown>
	</Wrapper>
)

LanguageDropDown.propTypes = {
	authToken: PropTypes.string,
	changeLanguage: PropTypes.func.isRequired,
	changeCardNameLanguage: PropTypes.func.isRequired,
	t: PropTypes.func.isRequired,
	userLanguage: PropTypes.string.isRequired
}

LanguageDropDown.defaultProps = {
	authToken: null
}

const Wrapper = styled.div`
	${Tag} {
		background: ${props => props.theme.secondary};
		border-radius: 2px;
		color: ${props => props.theme.white};
		display: inline;
		font-size: 0.7rem;
	}
`

const StyledLanguageDropDown = styled.button`
	background: transparent;
	border: 0;
	color: ${props => props.theme.text};
	cursor: pointer;
	padding: 0;
	transition: 0.2s;

	align-items: center;
	display: flex;
	justify-content: center;
	gap: 0.25rem;

	color: ${props => props.theme.textLight};
	font-size: 0.8rem;
	font-weight: 500;

	height: 30px;
	min-width: 30px;

	img {
		height: 16px;
		width: 16px;
	}

	${desktop`
		&:hover {
			opacity: 0.5;
		}
	`}
`

const DropDownLinkInner = styled.div`
	display: flex;
	flex-direction: column;
`

const DropDownLinkInnerTop = styled.div`
	align-items: center;
	display: flex;
	order: 1;
`

const DropDownLinkInnerBottom = styled.div`
	color: ${props => props.theme.textVeryLight};
	font-size: 0.7rem;
	font-weight: 500;
	order: 2;

	& + ${DropDownLinkInnerTop} {
		font-size: 0.9rem;
	}

	${props => props.onlyCardTextsHint && `
		margin-top: 0.1rem;
	`}
`

const Beta = styled.span`
	color: ${props => props.theme.orange};
	font-size: 0.8rem;
	font-weight: 600;
	margin-left: 0.35rem;
	text-transform: uppercase;
`

const TranslationsContribute = styled(DropDownLink)`
	span {
		font-size: 0.9rem;
		padding-right: 0.5rem;
		white-space: nowrap;
	}

	svg {
		height: 20px;
		width: 20px;
		min-width: 20px;

		margin-left: auto;
		margin-right: 0;
	}
`

const enhance = compose(
	connect(state => ({
		authToken: state.persist.authToken,
		userLanguage: state.persist.language
	}), dispatch => bindActionCreators({
		changeLanguage,
		changeCardNameLanguage
	}, dispatch)),
	translate('general'),
)

export default enhance(LanguageDropDown)
